import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { API, ASSETS, postEvent, waLink } from "@/lib/site";
import { REGIONS, getTreatments, GOALS, COUNTRIES, LANGS, TRAVEL_DATES, COMPANIONS } from "@/lib/mirrorConfig";
import { Seo } from "@/components/common";
import { BodyMap } from "@/components/BodyMap";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, MessageCircle, CheckCircle2, Loader2, Upload, FileText, X, Sparkles } from "lucide-react";

const STORAGE_KEY = "zeta_mirror_state";
const TOTAL_STEPS = 7;
const initialState = {
  step: 1,
  region: null,
  treatments: [],
  goal: null,
  name: "",
  phone: "",
  whatsapp: "",
  email: "",
  country: "",
  language: "",
  travelDate: "",
  companion: "",
  kvkk: false,
};

function StepShell({ t, step, go, submitting, title, subtitle, children, nextDisabled, onNext, nextLabel, hideNext }) {
  return (
    <div className="max-w-2xl mx-auto w-full fade-up" key={step}>
      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#F2F5F7]">{t(title)}</h2>
      {subtitle && <p className="mt-2 text-sm text-[#94A3AE]">{t(subtitle)}</p>}
      <div className="mt-8">{children}</div>
      <div className="flex items-center justify-between mt-10 pb-4">
        {step > 1 ? (
          <button onClick={() => go(step - 1)} data-testid="mirror-back-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#94A3AE] hover:text-[#F2F5F7] transition-colors py-3 px-2">
            <ArrowLeft size={15} /> {t({ tr: "Geri", en: "Back" })}
          </button>
        ) : (
          <span />
        )}
        {!hideNext && (
          <button onClick={onNext} disabled={nextDisabled || submitting} data-testid="mirror-next-btn" className="cta-primary disabled:opacity-40 disabled:transform-none disabled:shadow-none">
            {submitting && <Loader2 size={15} className="animate-spin" />}
            {t(nextLabel || { tr: "Devam et", en: "Continue" })}
          </button>
        )}
      </div>
    </div>
  );
}

function GuideBadge({ t, guide }) {
  if (!guide) return null;
  return (
    <p className="text-center text-xs font-semibold text-[var(--accent-2)] mb-6" data-testid="guide-continue-badge">
      {t({ tr: `${guide.toUpperCase()} ile devam ediyorsunuz`, en: `You are continuing with ${guide.toUpperCase()}` })}
    </p>
  );
}

export default function Mirror() {
  const { t, guide, setGuide } = useApp();
  const [s, setS] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return saved && saved.step <= TOTAL_STEPS ? { ...initialState, ...saved } : initialState;
    } catch {
      return initialState;
    }
  });
  const [files, setFiles] = useState([]);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    postEvent("mirror_started", { page: true });
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  }, [s]);

  const up = (patch) => setS((p) => ({ ...p, ...patch }));
  const go = (step) => setS((p) => ({ ...p, step }));
  const region = useMemo(() => REGIONS.find((r) => r.id === s.region) || null, [s.region]);
  const treatmentList = useMemo(() => (region ? getTreatments(region, guide) : []), [region, guide]);
  const progress = Math.round((s.step / TOTAL_STEPS) * 100);

  const toggleTreatment = (id) => {
    up({ treatments: s.treatments.includes(id) ? s.treatments.filter((x) => x !== id) : [...s.treatments, id] });
    postEvent("service_selected", { treatment: id });
  };

  const onFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    const valid = chosen.filter((f) => {
      const ok = /\.(jpe?g|png|pdf)$/i.test(f.name) && f.size <= 10 * 1024 * 1024;
      if (!ok) toast.error(t({ tr: `${f.name} desteklenmiyor veya 10MB'ı aşıyor`, en: `${f.name} is unsupported or exceeds 10MB` }));
      return ok;
    });
    setFiles((prev) => [...prev, ...valid.map((f) => ({ file: f, url: f.type.startsWith("image/") ? URL.createObjectURL(f) : null }))].slice(0, 3));
    e.target.value = "";
  };

  const removeFile = (i) => {
    setFiles((prev) => {
      if (prev[i]?.url) URL.revokeObjectURL(prev[i].url);
      return prev.filter((_, j) => j !== i);
    });
  };

  const submit = async () => {
    if (files.length > 0 && !consent) {
      toast.error(t({
        tr: "Fotoğrafları göndermek için sağlık verisi açık rızası gereklidir.",
        en: "Explicit health-data consent is required to submit photos.",
      }));
      return;
    }

    setSubmitting(true);
    try {
      const fileIds = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f.file);
        const r = await axios.post(`${API}/upload`, fd, {
          headers: { "X-Upload-Consent": "accepted" },
        });
        fileIds.push(r.data.file_id);
        postEvent("file_uploaded", {});
      }
      await axios.post(`${API}/leads`, {
        source: "mirror",
        guide,
        region: s.region,
        services: s.treatments,
        topics: s.goal ? [s.goal] : [],
        timeline: s.travelDate || null,
        country: s.country,
        language: s.language,
        contact_preference: s.whatsapp ? "whatsapp" : "phone",
        name: s.name,
        email: s.email,
        phone: s.phone,
        preferences: {
          goal: s.goal,
          whatsapp: s.whatsapp,
          companion: s.companion,
        },
        privacy_accepted: s.kvkk,
        consent,
        file_ids: fileIds,
      });
      postEvent("contact_submitted", { region: s.region, treatments: s.treatments.length });
      localStorage.removeItem(STORAGE_KEY);
      setDone(true);
      window.scrollTo(0, 0);
    } catch {
      toast.error(t({ tr: "Gönderim başarısız oldu. Seçimleriniz korundu, lütfen tekrar deneyin.", en: "Submission failed. Your selections are preserved, please try again." }));
    } finally {
      setSubmitting(false);
    }
  };

  const shellProps = { t, step: s.step, go, submitting };
  const inputCls = "w-full rounded-[10px] border border-white/15 bg-white/5 px-4 py-3 text-sm";
  const labelCls = "text-xs font-semibold text-[#94A3AE] block mb-1.5";

  if (done) {
    return (
      <main className="min-h-screen hero-mirror pt-28 pb-16 px-6" data-testid="mirror-page">
        <div className="max-w-xl mx-auto text-center fade-up" data-testid="mirror-confirmation">
          <CheckCircle2 size={52} className="mx-auto text-[var(--accent-2)]" />
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#F2F5F7] mt-6">
            {t({ tr: "Teşekkürler, talebiniz bize ulaştı", en: "Thank you, we received your request" })}
          </h2>
          <p className="mt-4 text-[#94A3AE] leading-relaxed">
            {t({ tr: "Sağlık koordinatörümüz 24 saat içinde size dönecek.", en: "Our health coordinator will get back to you within 24 hours." })}
          </p>
          <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="confirmation-whatsapp" onClick={() => postEvent("whatsapp_clicked", { from: "confirmation" })} className="mt-8 inline-flex items-center gap-2 rounded-[12px] bg-[#25D366] text-[#05070A] px-7 py-3.5 text-sm font-bold hover:opacity-90 transition-opacity">
            <MessageCircle size={16} /> {t({ tr: "WhatsApp'tan hemen yazın", en: "Message us on WhatsApp now" })}
          </a>
          <div className="mt-6">
            <Link to="/" className="text-sm text-[#94A3AE] underline underline-offset-4">{t({ tr: "Ana sayfaya dön", en: "Back to home" })}</Link>
          </div>
          <p className="mt-12 text-xs text-[#7C8B97]" data-testid="mirror-disclaimer">
            {t({
              tr: "ZETA Mirror yalnızca ilgi alanlarınızı belirlemeye yardımcı olur. Kesin tedavi planı ve uygunluk değerlendirmesi, uzman doktor muayenesinden sonra oluşturulur.",
              en: "ZETA Mirror only helps identify your areas of interest. The definitive treatment plan and suitability assessment are established after examination by a specialist doctor.",
            })}
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen hero-mirror pt-28 pb-16 px-6" data-testid="mirror-page">
      <Seo title="ZETA Mirror" description={t({ tr: "Etkileşimli ön değerlendirme: rehberinizi ve bölgenizi seçin, tedavilerinizi işaretleyin, kişiye özel teklif alın.", en: "Interactive pre-assessment: choose your guide and area, select treatments, receive a personalised offer." })} />

      <div className="max-w-2xl mx-auto mb-8 sticky top-[76px] z-20 glass-card px-5 py-3">
        <div className="flex items-center justify-between text-xs font-semibold text-[#94A3AE] mb-2">
          <span className="section-label !mb-0">ZETA MIRROR</span>
          <span data-testid="mirror-progress-label">{t({ tr: "Adım", en: "Step" })} {s.step}/{TOTAL_STEPS}</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-[var(--accent)] to-[var(--accent-2)] transition-[width] duration-500" style={{ width: `${progress}%` }} data-testid="mirror-progress-bar" />
        </div>
      </div>

      {/* STEP 1 — guide */}
      {s.step === 1 && (
        <StepShell {...shellProps} title={{ tr: "Yolculuğunda sana kim eşlik etsin?", en: "Who should accompany you on your journey?" }} subtitle={{ tr: "Rehber seçimi hizmetleri değiştirmez; yalnızca deneyimin rengini ve rehberini belirler.", en: "Guide choice never changes the services; it only sets the colour and companion of your experience." }} hideNext>
          <div className="grid grid-cols-2 gap-4">
            {[
              { id: "zeta", img: ASSETS.zetaChar, color: "#2ECBD9" },
              { id: "atez", img: ASSETS.atezChar, color: "#A8D93B" },
            ].map((c) => (
              <button
                key={c.id}
                data-testid={`mirror-guide-${c.id}`}
                onClick={() => {
                  setGuide(c.id);
                  go(2);
                  postEvent("guide_selected", { guide: c.id, from: "mirror" });
                }}
                className={`glass-card p-5 text-center transition-transform hover:-translate-y-1 ${guide === c.id ? "ring-1" : ""}`}
                style={guide === c.id ? { borderColor: c.color, "--tw-ring-color": c.color } : {}}
              >
                <div className="h-44 flex items-end justify-center mb-3" style={{ background: `radial-gradient(ellipse at 50% 40%, ${c.color}1c, transparent 72%)` }}>
                  <img src={c.img} alt={c.id.toUpperCase()} className="h-40 w-auto object-contain" />
                </div>
                <span className="font-heading font-bold tracking-wide" style={{ color: c.color }}>{c.id.toUpperCase()}</span>
              </button>
            ))}
          </div>
        </StepShell>
      )}

      {/* STEP 2 — region */}
      {s.step === 2 && (
        <StepShell {...shellProps} title={{ tr: "Kendinizde en çok nereyi değiştirmek veya geliştirmek istersiniz?", en: "What would you most like to change or improve?" }} hideNext>
          <BodyMap
            onSelect={(r) => {
              up({ region: r.id, treatments: [], step: 3 });
              postEvent("region_selected", { region: r.id });
            }}
          />
        </StepShell>
      )}

      {/* STEP 3 — treatments */}
      {s.step === 3 && region && (
        <StepShell {...shellProps} title={{ tr: `${t(region.label)} — tedavini seç`, en: `${t(region.label)} — choose your treatment` }} subtitle={{ tr: "Birden fazla seçim yapabilirsiniz.", en: "You can select more than one." }} nextDisabled={s.treatments.length === 0} onNext={() => { go(4); postEvent("intake_started", {}); }}>
          <GuideBadge t={t} guide={guide} />
          {region.featured && (
            <p className="mb-4 inline-flex items-center gap-1.5 text-xs font-bold text-[var(--accent-2)] bg-[var(--accent-soft)] rounded-full px-3 py-1.5">
              <Sparkles size={12} /> {t({ tr: "Öne Çıkan Tedavi Alanı", en: "Featured Treatment Area" })}
            </p>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            {treatmentList.map((tr) => (
              <button
                key={tr.id}
                data-testid={`treatment-${tr.id}`}
                onClick={() => toggleTreatment(tr.id)}
                aria-pressed={s.treatments.includes(tr.id)}
                className={`glass-card p-4 text-left text-sm font-medium transition-transform hover:-translate-y-0.5 ${s.treatments.includes(tr.id) ? "!border-[var(--accent)] ring-1 ring-[var(--accent)] text-[#F2F5F7]" : "text-[#C4CFD6]"}`}
              >
                {t(tr.label)}
              </button>
            ))}
          </div>
        </StepShell>
      )}

      {/* STEP 4 — goal */}
      {s.step === 4 && (
        <StepShell {...shellProps} title={{ tr: "Hedefini belirle", en: "Set your goal" }} nextDisabled={!s.goal} onNext={() => go(5)}>
          <GuideBadge t={t} guide={guide} />
          <div className="grid sm:grid-cols-2 gap-3">
            {GOALS.map((gl) => (
              <button key={gl.id} data-testid={`goal-${gl.id}`} onClick={() => up({ goal: gl.id })} aria-pressed={s.goal === gl.id} className={`glass-card p-4 text-left text-sm font-medium ${s.goal === gl.id ? "!border-[var(--accent)] ring-1 ring-[var(--accent)] text-[#F2F5F7]" : "text-[#C4CFD6]"}`}>
                {t(gl.label)}
              </button>
            ))}
          </div>
        </StepShell>
      )}

      {/* STEP 5 — photos */}
      {s.step === 5 && (
        <StepShell {...shellProps} title={{ tr: "İsterseniz fotoğraf ekleyin", en: "Optionally add photos" }} subtitle={{ tr: "Zorunlu değildir. En fazla 3 dosya · jpg, png, pdf · her biri en fazla 10MB. Fotoğraflarınız asla herkese açık gösterilmez.", en: "Not required. Up to 3 files · jpg, png, pdf · max 10MB each. Your photos are never shown publicly." }} onNext={() => go(6)}>
          <label className="flex items-start gap-3 glass-card p-5 cursor-pointer">
            <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="consent-checkbox" className="mt-0.5" />
            <span className="text-sm text-[#94A3AE] leading-relaxed">
              {t({ tr: "Sağlık verilerimin değerlendirme amacıyla işlenmesine ve yurt dışına aktarılmasına açık rıza veriyorum.", en: "I give explicit consent for my health data to be processed for evaluation purposes and transferred abroad." })}{" "}
              <Link to="/kvkk" className="underline text-[var(--accent-2)]">{t({ tr: "KVKK metni", en: "KVKK notice" })}</Link>
            </span>
          </label>

          <label className={`mt-5 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${consent ? "border-[var(--accent)]/40 bg-[var(--accent-soft)] cursor-pointer hover:border-[var(--accent)]" : "border-white/10 bg-white/[0.02] cursor-not-allowed opacity-60"}`}>
            <Upload size={22} className="text-[var(--accent-2)]" />
            <span className="text-sm font-semibold text-[#F2F5F7]">{t({ tr: "Dosya seçin", en: "Choose files" })}</span>
            <span className="text-xs text-[#94A3AE]">{consent ? t({ tr: "jpg, png veya pdf", en: "jpg, png or pdf" }) : t({ tr: "Önce açık rıza onayı gereklidir", en: "Explicit consent is required first" })}</span>
            <input type="file" multiple accept=".jpg,.jpeg,.png,.pdf" disabled={!consent} onChange={onFiles} className="hidden" data-testid="file-input" />
          </label>

          {files.length > 0 && (
            <ul className="mt-4 grid grid-cols-3 gap-3" data-testid="file-list">
              {files.map((f, i) => (
                <li key={i} className="relative glass-card p-2 flex flex-col items-center gap-1.5">
                  {f.url ? (
                    <img src={f.url} alt={f.file.name} className="w-full h-20 object-cover rounded-lg" />
                  ) : (
                    <span className="w-full h-20 flex items-center justify-center"><FileText size={26} className="text-[#94A3AE]" /></span>
                  )}
                  <span className="text-[10px] text-[#94A3AE] truncate w-full text-center">{f.file.name}</span>
                  <button onClick={() => removeFile(i)} data-testid={`file-remove-${i}`} aria-label={t({ tr: "Dosyayı kaldır", en: "Remove file" })} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center">
                    <X size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </StepShell>
      )}

      {/* STEP 6 — contact */}
      {s.step === 6 && (
        <StepShell {...shellProps} title={{ tr: "İletişim bilgileriniz", en: "Your contact details" }} nextDisabled={!s.name.trim() || !/\S+@\S+\.\S+/.test(s.email) || !s.phone.trim() || !s.country || !s.language} onNext={() => go(7)}>
          <GuideBadge t={t} guide={guide} />
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className={labelCls}>{t({ tr: "Ad Soyad *", en: "Full name *" })}</label>
              <input data-testid="contact-name" value={s.name} onChange={(e) => up({ name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{t({ tr: "Telefon *", en: "Phone *" })}</label>
              <input data-testid="contact-phone" value={s.phone} onChange={(e) => up({ phone: e.target.value })} placeholder="+49 ..." className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{t({ tr: "WhatsApp numarası", en: "WhatsApp number" })}</label>
              <input data-testid="contact-whatsapp" value={s.whatsapp} onChange={(e) => up({ whatsapp: e.target.value })} placeholder="+49 ..." className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>{t({ tr: "E-posta *", en: "Email *" })}</label>
              <input data-testid="contact-email" type="email" value={s.email} onChange={(e) => up({ email: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{t({ tr: "Yaşadığınız ülke *", en: "Country of residence *" })}</label>
              <select data-testid="country-select" value={s.country} onChange={(e) => up({ country: e.target.value })} className={inputCls}>
                <option value="">{t({ tr: "Seçin", en: "Select" })}</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>{t({ tr: "Tercih ettiğiniz dil *", en: "Preferred language *" })}</label>
              <select data-testid="language-select" value={s.language} onChange={(e) => up({ language: e.target.value })} className={inputCls}>
                <option value="">{t({ tr: "Seçin", en: "Select" })}</option>
                {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>{t({ tr: "Tahmini seyahat tarihi", en: "Estimated travel date" })}</label>
              <select data-testid="travel-date-select" value={s.travelDate} onChange={(e) => up({ travelDate: e.target.value })} className={inputCls}>
                <option value="">{t({ tr: "Seçin", en: "Select" })}</option>
                {TRAVEL_DATES.map((d) => <option key={d.id} value={d.id}>{t(d.label)}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>{t({ tr: "Refakatçi", en: "Companion" })}</label>
              <select data-testid="companion-select" value={s.companion} onChange={(e) => up({ companion: e.target.value })} className={inputCls}>
                <option value="">{t({ tr: "Seçin", en: "Select" })}</option>
                {COMPANIONS.map((c) => <option key={c.id} value={c.id}>{t(c.label)}</option>)}
              </select>
            </div>
          </div>
        </StepShell>
      )}

      {/* STEP 7 — summary */}
      {s.step === 7 && (
        <StepShell {...shellProps} title={{ tr: "Özet ve gönderim", en: "Summary and submission" }} hideNext>
          <GuideBadge t={t} guide={guide} />
          <div className="glass-card p-6 space-y-3 text-sm" data-testid="mirror-summary">
            {[
              [{ tr: "Rehber", en: "Guide" }, (guide || "-").toUpperCase()],
              [{ tr: "Bölge", en: "Area" }, region ? t(region.label) : "-"],
              [{ tr: "Tedaviler", en: "Treatments" }, treatmentList.filter((x) => s.treatments.includes(x.id)).map((x) => t(x.label)).join(", ") || "-"],
              [{ tr: "Hedef", en: "Goal" }, t(GOALS.find((g) => g.id === s.goal)?.label || "-")],
              [{ tr: "Ülke / Tarih", en: "Country / Date" }, `${s.country || "-"} · ${t(TRAVEL_DATES.find((d) => d.id === s.travelDate)?.label || { tr: "Belirtilmedi", en: "Not set" })}`],
              [{ tr: "İletişim", en: "Contact" }, `${s.name} · ${s.email} · ${s.phone}`],
              [{ tr: "Dosya", en: "Files" }, `${files.length}`],
            ].map(([k, v], i) => (
              <div key={i} className="flex gap-4 justify-between border-b border-white/5 pb-2 last:border-0 last:pb-0">
                <span className="text-[#94A3AE] shrink-0">{t(k)}</span>
                <span className="text-[#F2F5F7] text-right">{v}</span>
              </div>
            ))}
          </div>
          <label className="flex items-start gap-3 glass-card p-4 mt-6 cursor-pointer">
            <Checkbox checked={s.kvkk} onCheckedChange={(v) => up({ kvkk: !!v })} data-testid="kvkk-consent-checkbox" className="mt-0.5" />
            <span className="text-sm text-[#94A3AE] leading-relaxed">
              {t({ tr: "Kişisel verilerimin ön değerlendirme amacıyla işlenmesini kabul ediyorum.", en: "I accept the processing of my personal data for pre-assessment purposes." })}{" "}
              <Link to="/kvkk" className="underline text-[var(--accent-2)]">{t({ tr: "KVKK metni", en: "KVKK notice" })}</Link>
            </span>
          </label>
          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <button onClick={submit} disabled={submitting || !s.kvkk} data-testid="submit-lead-btn" className="cta-primary flex-1 justify-center disabled:opacity-40">
              {submitting && <Loader2 size={15} className="animate-spin" />}
              {t({ tr: "Ücretsiz Ön Değerlendirmemi Başlat", en: "Start My Free Pre-Assessment" })}
            </button>
            <a href={waLink(t({ tr: "Merhaba, ZETA Mirror üzerinden geliyorum.", en: "Hello, I'm coming from ZETA Mirror." }))} target="_blank" rel="noopener noreferrer" data-testid="summary-whatsapp-btn" onClick={() => postEvent("whatsapp_clicked", { from: "summary" })} className="cta-ghost flex-1 justify-center">
              <MessageCircle size={15} /> {t({ tr: "WhatsApp'tan Koordinatöre Bağlan", en: "Connect to Coordinator on WhatsApp" })}
            </a>
          </div>
        </StepShell>
      )}

      <p className="max-w-2xl mx-auto mt-14 text-center text-xs text-[#7C8B97]" data-testid="mirror-disclaimer">
        {t({
          tr: "ZETA Mirror yalnızca ilgi alanlarınızı belirlemeye yardımcı olur; tanı koymaz ve tedavi sonucu garanti etmez. Kesin tedavi planı ve uygunluk değerlendirmesi, uzman doktor muayenesinden sonra oluşturulur.",
          en: "ZETA Mirror only helps identify your areas of interest; it does not diagnose and does not guarantee outcomes. The definitive treatment plan and suitability assessment are established after examination by a specialist doctor.",
        })}
      </p>
    </main>
  );
}
